import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import Turnstile, { useTurnstile } from 'react-turnstile';

import { PrimaryButton } from '@components/Button/variants';
import FieldError from '@components/Form/FieldError';
import { useUpdateStripeCustomerLocation, useVerifyCloudflareTurnstile } from '@hooks';
import { CLOUDFLARE_SITE_KEY } from '@src/env';
import { AddressElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Address } from '@stripe/stripe-js';
import { buildQueryString } from '@utils/queryStringHelper';
import {
  ADD_PAYMENT_METHOD_FALLBACK_ERROR,
  BILLING_ADDRESS_FALLBACK_ERROR,
} from '@utils/stripePaymentError';

interface PaymentMethodProps {
  stripeCustomerId: string;
}

interface FormValues {
  username: string;
}

const ADD_PAYMENT_METHOD = 'add_payment_method';

const NewPaymentMethod = ({ stripeCustomerId }: PaymentMethodProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const turnstile = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const { mutateAsync: updateCustomerLocation } = useUpdateStripeCustomerLocation();
  const { mutateAsync: verifyCloudflareTurnstile } = useVerifyCloudflareTurnstile();

  const stripe = useStripe();
  const elements = useElements();
  const protocol = window.location.protocol;
  const host = window.location.host;

  const updateCustomerLocationDetails = async (
    customerDetails: { address: Address; name: string },
    stripeCustomerId: string
  ) =>
    await updateCustomerLocation({
      name: customerDetails.name,
      stripe_customer_id: stripeCustomerId,
      address: customerDetails.address,
    });

  const resetTurnstile = () => {
    setTurnstileToken(null);
    turnstile.reset();
  };

  const handleError = (message: string) => {
    setErrorMessage(message);
    setIsLoading(false);
    resetTurnstile();
  };

  const handleSubmit = async ({ username }: FormValues) => {
    // Username is a random hidden field to prevent bots from submitting the form,
    // human users will not see this but bots will so if the field is filled out,
    // we know it's a bot and we don't submit the form.
    if (username) {
      return;
    }

    try {
      if (!turnstileToken) {
        handleError('Please complete the verification check and try again.');
        return;
      }

      const { success: turnstileSuccess } = await verifyCloudflareTurnstile({
        turnstile_token: turnstileToken,
        turnstile_action: ADD_PAYMENT_METHOD,
      });

      if (!turnstileSuccess) {
        handleError("We couldn't verify this request. Please complete the check and try again.");
        return;
      }

      if (!stripe || !elements) {
        handleError('Still loading Stripe. Please wait a few seconds and then try again.');
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError.message || ADD_PAYMENT_METHOD_FALLBACK_ERROR);
        return;
      }

      const addressElement = elements.getElement(AddressElement);
      if (!addressElement) {
        handleError('Billing address is still loading. Please wait a moment and try again.');
        return;
      }

      const { value: customerDetails } = await addressElement.getValue();
      const { success } = await updateCustomerLocationDetails(customerDetails, stripeCustomerId);

      if (!success) {
        handleError(BILLING_ADDRESS_FALLBACK_ERROR);
        return;
      }

      const queryString = buildQueryString({
        update_payment: true,
      });
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          // Can't use READER_URL for testing, so this is used to build URLs dynamically
          return_url: `${protocol}//${host}/payment-status?${queryString}`,
        },
        redirect: 'always',
      });

      if (error) {
        handleError(error.message || ADD_PAYMENT_METHOD_FALLBACK_ERROR);
        return;
      }
    } catch (error) {
      const apiMessage = (error as { message?: string })?.message;
      handleError(apiMessage || ADD_PAYMENT_METHOD_FALLBACK_ERROR);
    }
  };

  return (
    <Formik initialValues={{ username: '' }} onSubmit={handleSubmit}>
      {() => (
        <Form className="w-full sm:w-[440px]">
          <div id="address-form" className="stripe-address-form mb-20 py-32">
            <AddressElement
              options={{
                mode: 'billing',
                display: {
                  name: 'split',
                },
              }}
            />
          </div>
          <div id="payment-form" className="stripe-payment-form">
            <PaymentElement
              onChange={() => setErrorMessage('')}
              options={{
                layout: {
                  type: 'tabs',
                  defaultCollapsed: false,
                },
              }}
            />
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
            <Turnstile
              className="mt-8"
              fixedSize
              sitekey={CLOUDFLARE_SITE_KEY}
              theme="light"
              refreshExpired="auto"
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={resetTurnstile}
              onError={resetTurnstile}
            />
            <PrimaryButton
              as="button"
              fullWidth
              disabled={!stripe || isLoading}
              type="submit"
              className="mt-16"
              isLoading={isLoading}
            >
              Add payment method
            </PrimaryButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default NewPaymentMethod;
